"use client";

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from "react";

import { Icon } from "@core/components/Icon";
import { LinkBehaviour } from "@core/components/LinkBehaviour";
import { Loader } from "@core/components/Loader";
import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";

import styles from "./Button.module.scss";

export const BUTTON_VARIANTS = ["primary", "secondary", "ghost", "text", "text-muted"] as const;
export const BUTTON_SIZES = ["lg", "md", "sm"] as const;

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonSize = (typeof BUTTON_SIZES)[number];

type ButtonBaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  iconBefore?: DirectionalIconComponent;
  iconAfter?: DirectionalIconComponent;
  pending?: boolean;
  /**
   * Escape hatch for custom styling
   *
   * @deprecated Wrap the Button in a styled element instead of passing `className`
   */
  className?: string;
  children?: string;
  /**
   * Escape hatch for rendering non-text content (e.g. an inline image).
   * Use only as a last resort — prefer `children` with a string label.
   *
   * @deprecated Pass a string via `children` instead
   */
  richContent?: ReactNode;
};

export type ButtonLinkProps = ButtonBaseProps & {
  href: string;
  nativeBehaviour?: boolean;
  disabled?: boolean;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children">;

export type ButtonButtonProps = ButtonBaseProps & {
  href?: never;
  ref?: Ref<HTMLButtonElement>;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

export type ButtonProps = ButtonLinkProps | ButtonButtonProps;

const iconSizeByButtonSize: Record<ButtonSize, "xs" | "sm" | "md"> = {
  lg: "md",
  md: "sm",
  sm: "xs",
};

export const Button = (props: ButtonProps) => {
  const {
    children,
    richContent,
    variant = "primary",
    size = "md",
    fullWidth = false,
    iconBefore: IconBefore,
    iconAfter: IconAfter,
    pending = false,
    disabled,
  } = props;
  const label = children ?? richContent;
  const isDisabled = disabled || pending;
  const classes = classnames(styles.button, styles[variant], styles[`size-${size}`], props.className, {
    [styles.pending]: pending,
    [styles.fullWidth]: fullWidth,
  });

  const content = (
    <>
      <span className={styles.content}>
        {!pending && IconBefore ? <Icon icon={IconBefore} size={iconSizeByButtonSize[size]} /> : null}
        {label && <span className={styles.label}>{label}</span>}
        {!pending && IconAfter ? <Icon icon={IconAfter} size={iconSizeByButtonSize[size]} /> : null}
      </span>
      {pending ? (
        <span className={styles.loader}>
          <Loader size="sm" isInverse={variant === "primary"} data-testid="button-loader" aria-hidden="true" />
        </span>
      ) : null}
    </>
  );

  if (typeof props.href === "string") {
    const {
      variant: _variant,
      size: _size,
      fullWidth: _fullWidth,
      iconBefore: _iconBefore,
      iconAfter: _iconAfter,
      pending: _pending,
      disabled: _disabled,
      className: _className,
      children: _children,
      richContent: _richContent,
      onClick,
      tabIndex,
      ...linkProps
    } = props;

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }

      onClick?.(event);
    };

    return (
      <LinkBehaviour
        {...linkProps}
        className={classes}
        aria-busy={pending || undefined}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : tabIndex}
        onClick={handleClick}
      >
        {content}
      </LinkBehaviour>
    );
  }

  const {
    variant: _variant,
    size: _size,
    fullWidth: _fullWidth,
    iconBefore: _iconBefore,
    iconAfter: _iconAfter,
    pending: _pending,
    className: _className,
    children: _children,
    richContent: _richContent,
    ref,
    type = "button",
    ...buttonProps
  } = props;

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={classes}
      type={type}
      disabled={isDisabled}
      aria-busy={pending || undefined}
    >
      {content}
    </button>
  );
};
